import { useMemo } from 'react';
import { useRaporlar } from './hooks/useRaporlar';
import { bakimHesapla } from '@/pages/bakim-takip/utils/bakimHesapla';
import OzetKartlar from './components/OzetKartlar';
import KazancGrafigi from './components/KazancGrafigi';
import IslemDagilimiKart from './components/IslemDagilimi';
import AylikPerformansTablosu from './components/AylikPerformansTablosu';

export default function RaporlarPage() {
  const { servisler, musteriler, loading, error, refetch, getAylikVeriler, getIslemDagilimi, getOdemeOzeti, getGenelOzet } = useRaporlar();

  const aylikVeriler = useMemo(() => getAylikVeriler(12), [servisler]);
  const islemDagilim = useMemo(() => getIslemDagilimi(), [servisler]);
  const odemeler = useMemo(() => getOdemeOzeti(), [servisler]);
  const ozet = useMemo(() => getGenelOzet(), [servisler]);

  // Bakım durumları - tüm müşterilerden
  const [bakimDurumlar, setBakimDurumlar] = useMemo(() => {
    // Bakım durumlarını hesaplamak için servislerden son bakım tarihlerini alalım
    const musteriSonBakim: Record<string, string> = {};
    servisler.forEach((s: any) => {
      if (s.servis_tarihi && (!musteriSonBakim[s.musteri_id] || s.servis_tarihi > musteriSonBakim[s.musteri_id])) {
        musteriSonBakim[s.musteri_id] = s.servis_tarihi;
      }
    });

    let yesil = 0, sari = 0, kirmizi = 0, bakimYok = 0;
    musteriler.forEach((m: any) => {
      const sonBakim = musteriSonBakim[m.id];
      if (!sonBakim) { bakimYok++; return; }
      const durum = bakimHesapla(sonBakim).durum;
      if (durum === 'yesil') yesil++;
      else if (durum === 'sari') sari++;
      else kirmizi++;
    });

    return { yesil, sari, kirmizi, bakimYok };
  }, [servisler, musteriler]);

  if (loading) {
    return (
      <div className="text-center py-16">
        <i className="ri-loader-4-line animate-spin text-3xl text-sky-500"></i>
        <p className="text-sm text-gray-500 mt-3">Rapor verileri yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-center">
        <p className="text-sm text-red-700 font-medium">Veriler yüklenemedi.</p>
        <button onClick={refetch} className="mt-3 text-sm text-red-600 font-semibold cursor-pointer">Tekrar Dene</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Raporlar & İstatistikler</h2>
          <p className="text-sm text-gray-500 mt-0.5">Tüm servis ve gelir verilerinin özeti</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500 bg-white border border-gray-100 rounded-xl px-4 py-2.5">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0"><i className="ri-calendar-line text-gray-400"></i></div>
          <span>Son güncelleme: {new Date().toLocaleDateString('tr-TR')}</span>
        </div>
      </div>

      <OzetKartlar ozet={ozet} />

      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'İyi Durumda', sayi: bakimDurumlar.yesil, bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', dot: 'bg-emerald-500', pct: musteriler.length > 0 ? Math.round((bakimDurumlar.yesil / musteriler.length) * 100) : 0 },
          { label: 'Bakım Yaklaşıyor', sayi: bakimDurumlar.sari, bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', dot: 'bg-yellow-500', pct: musteriler.length > 0 ? Math.round((bakimDurumlar.sari / musteriler.length) * 100) : 0 },
          { label: 'Acil Bakım', sayi: bakimDurumlar.kirmizi, bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', dot: 'bg-red-500', pct: musteriler.length > 0 ? Math.round((bakimDurumlar.kirmizi / musteriler.length) * 100) : 0 },
        ].map(item => (
          <div key={item.label} className={`${item.bg} border ${item.border} rounded-xl p-4`}>
            <div className="flex items-center gap-2 mb-2">
              <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${item.dot}`}></span>
              <span className={`text-xs font-semibold ${item.text}`}>{item.label}</span>
            </div>
            <p className={`text-2xl font-bold ${item.text}`}>{item.sayi}</p>
            <p className={`text-xs mt-1 ${item.text} opacity-70`}>%{item.pct} müşteri</p>
            <div className="mt-2 h-1.5 bg-white/60 rounded-full overflow-hidden">
              <div className={`h-1.5 rounded-full ${item.dot}`} style={{ width: `${item.pct}%` }} />
            </div>
          </div>
        ))}
      </div>

      <KazancGrafigi veriler={aylikVeriler} />

      <IslemDagilimiKart dagilim={islemDagilim} odemeler={odemeler} toplamServis={ozet.toplamServis} />

      <AylikPerformansTablosu veriler={aylikVeriler} />

      {/* Son 5 Servis */}
      <div className="bg-white rounded-xl border border-gray-100">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-bold text-gray-800">Son Servis Kayıtları</h3>
          <a href="/servis-kayitlari" className="text-xs text-sky-600 hover:text-sky-800 font-semibold cursor-pointer">Tümünü Görüntüle →</a>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-slate-50">
                <th className="text-left px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Müşteri</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell">İşlem</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Tarih</th>
                <th className="text-right px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Ücret</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {servisler.slice(0, 5).length === 0 ? (
                <tr><td colSpan={4} className="text-center py-8 text-sm text-gray-400">Henüz servis kaydı bulunmuyor.</td></tr>
              ) : servisler.slice(0, 5).map((s: any) => {
                const islemRenk: Record<string, string> = {
                  'Filtre Bakımı – Dörtlü': 'bg-sky-100 text-sky-700',
                  'Filtre Bakımı – Beşli': 'bg-indigo-100 text-indigo-700',
                  'Genel Bakım': 'bg-emerald-100 text-emerald-700',
                  'Yeni Montaj': 'bg-violet-100 text-violet-700',
                  'Bina Girişi': 'bg-teal-100 text-teal-700',
                  'Bina Girişi Bakım': 'bg-cyan-100 text-cyan-700',
                  'Taşıma': 'bg-orange-100 text-orange-700',
                  'Diğer': 'bg-gray-100 text-gray-700',
                };
                return (
                  <tr key={s.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-3"><p className="text-sm font-semibold text-gray-800">{s.musteri_id?.substring(0, 8)}</p></td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${islemRenk[s.islem_turu] || 'bg-gray-100 text-gray-600'}`}>{s.islem_turu}</span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-sm text-gray-500">{s.servis_tarihi ? new Date(s.servis_tarihi).toLocaleDateString('tr-TR') : '-'}</span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <span className="text-sm font-bold text-emerald-600">₺{(Number(s.alinan_ucret) || 0).toLocaleString('tr-TR')}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}